import * as React from 'react'
import { FormikHelpers } from 'formik'
import styled from 'styled-components'

import {
  ConfirmZipProps,
  ConfirmZipOutgoingPayload,
} from '@ally/transmitigator'
import {
  Box,
  Button,
  Icon,
  Space,
  TextBody,
  TextHeading,
} from '@ally/metronome-ui'
import { muiAuto } from '@ally/metronome-icons'
import { Form, Forms, Zip } from '@ally/forms'

import { useTransmit } from '../../TransmitContext'
import { CIAMErrorBanner, InputWrapper, StyledTextBody } from '../Shared'
import { CallUsModal } from '../../Modals'

const IconHeadingWrapper = styled(Box)`
  display: flex;
  align-items: center;
`

// The CIAM backend only supports the 5 digit ZIP code, not ZIP+4, so we need to limit it here
const ZIP_PATTERN = /^\d{5}$/

export const zipValidator = (
  value: string,
  required = false,
): string | undefined => {
  const isValidZip = RegExp(ZIP_PATTERN).test(value)

  if (required && !value) {
    return 'Enter a ZIP Code to continue.'
  }

  if (value && !isValidZip) {
    return 'Enter a valid ZIP Code.'
  }

  return undefined
}

const ConfirmZipContent: React.FC<ConfirmZipProps> = ({
  onComplete,
  status,
  error,
}) => {
  const formId = 'confirm-zip-form'
  const initialValues = {
    zipCode: '',
  }

  const handleSubmit = (
    { zipCode }: ConfirmZipOutgoingPayload,
    { setSubmitting }: FormikHelpers<ConfirmZipOutgoingPayload>,
  ): void => {
    onComplete({
      zipCode,
    })

    setSubmitting(false)
  }

  return (
    <>
      <IconHeadingWrapper mb="md">
        <Icon
          fill="black"
          icon={muiAuto}
          unsupportedCustomWidth={42}
          unsupportedCustomHeight={42}
          mr="sm"
        />
        <Space mt="xs">
          <TextHeading
            focusOnMount
            tag="h1"
            size="md"
            id="skip-target"
            tabIndex={-1}
          >
            New wheels? Nice.
          </TextHeading>
        </Space>
      </IconHeadingWrapper>

      <Space mb="lg">
        <StyledTextBody size="sm" tag="p">
          We noticed you may have recently added a new Ally Auto account.{' '}
          <TextBody size="sm" tag="b" weight="bold">
            Just confirm your ZIP code
          </TextBody>{' '}
          and we&apos;ll connect it to your Snapshot. If you didn&apos;t add an
          Auto account, <CallUsModal triggerContent="give us a call" />.
        </StyledTextBody>
      </Space>

      <CIAMErrorBanner error={error} />

      <Forms
        formId={formId}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        <Form id={formId}>
          <InputWrapper mb="md">
            <Zip
              label="Enter the ZIP code of your account billing address."
              name="zipCode"
              required
              validate={zipValidator}
            />
          </InputWrapper>

          <Button
            allytmln="addAccount"
            text="Add Account"
            loading={status === 'Loading'}
            type="submit"
            variant="primary"
          />
        </Form>
      </Forms>
    </>
  )
}

export const ConfirmZip: React.FC = () => {
  const { activeScreen } = useTransmit()

  if (activeScreen.type !== 'ConfirmZip') {
    return null
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <ConfirmZipContent {...activeScreen.payload} />
}
